<script lang="ts">
	import { afterNavigate, beforeNavigate } from '$app/navigation';
	import { page, updated } from '$app/state';
	import { ClientVelatlasLoggerApi } from '$lib/api/velatlas/velatlas.loggerApi.client';
	import { createScrollTracker } from '$lib/attachments/scrollTracker';
	import Footer from '$lib/components/Footer.svelte';
	import NavBar from '$lib/components/NavBar.svelte';
	import BackToTop from '$lib/components/ui/BackToTop.svelte';
	import { ANALYTICS_EVENT_NAMES } from '$lib/statics';
	import { getBotName, getCleanReferrer } from '$lib/utils/analytics.util';
	import '@fontsource-variable/inter/wght.css';
	import '@fontsource-variable/montserrat/wght.css';
	import '@fontsource/cormorant-garamond/index.css';
	import { onDestroy, onMount } from 'svelte';
	import '../app.css';

	let { children } = $props();

	const isOverlay = $derived.by<boolean>(() => {
		const overlayHeader: undefined | boolean = page.data?.output?.overlayHeader;
		const overlayHeaderExists = overlayHeader !== undefined;
		return overlayHeaderExists ? overlayHeader : false;
	});

	const analyticsApi = new ClientVelatlasLoggerApi();
	let isPageEntryTracked: boolean = false;
	let isInitialSiteEntry = true;

	const scrollTracker = createScrollTracker();
	let engagementTimer = $state({
		activeSeconds: 0,
		lastActiveTime: 0,
		isPaused: false,
		uri: '',
		url: ''
	});
	let hasInteracted = $state<boolean>(false);

	function isBot(): boolean {
		const botPattern = /bot|crawler|spider|crawling|lighthouse|headless/i;
		return typeof navigator !== 'undefined' && botPattern.test(navigator.userAgent);
	}

	function updateActiveTime() {
		if (engagementTimer.isPaused || isBot() || !hasInteracted) return;
		const now = performance.now();
		engagementTimer.activeSeconds += (now - engagementTimer.lastActiveTime) / 1000;
		engagementTimer.lastActiveTime = now;
	}

	function sendDurationAnalytics(keepalive = false) {
		updateActiveTime();
		const roundedSeconds = Math.round(engagementTimer.activeSeconds);
		if (!hasInteracted || roundedSeconds <= 0 || isBot()) return;

		const fallbackRoute = ClientVelatlasLoggerApi.getRouteMetadata();
		analyticsApi.sendAnalytics(
			{
				event: ANALYTICS_EVENT_NAMES.pageEngagement,
				payload: { sec: roundedSeconds },
				uri: engagementTimer.uri || fallbackRoute.uri,
				url: engagementTimer.url || fallbackRoute.url
			},
			{ keepalive }
		);
		engagementTimer.activeSeconds = 0;
	}

	function trackPageEntry() {
		if (isBot() || isPageEntryTracked) return;
		isPageEntryTracked = true;

		const payload: Record<string, any> = {};

		if (isInitialSiteEntry) {
			isInitialSiteEntry = false;
			const referrer = getCleanReferrer();
			payload.referrer = referrer ?? 'direct';
		}

		analyticsApi.sendAnalytics({
			event: ANALYTICS_EVENT_NAMES.pageEntry,
			payload: Object.keys(payload).length > 0 ? payload : undefined
		});
	}

	function markAsHumanInteraction() {
		if (hasInteracted) return;
		hasInteracted = true;
		engagementTimer.lastActiveTime = performance.now();
		trackPageEntry(); // Send entry immediately upon interaction
	}

	function handleExternalLinkClick(event: MouseEvent) {
		const link = (event.target as HTMLElement).closest('a');
		if (!link) return;

		const href = link.href;

		// Ignore empty links, page hashes, email mailto's, and telephone triggers
		if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

		try {
			const targetUrl = new URL(href);
			const isExternalPage = targetUrl.host !== window.location.host;
			if (isExternalPage) {
				analyticsApi.sendAnalytics({ event: ANALYTICS_EVENT_NAMES.externalClick, payload: { url: href } }, { keepalive: true });
			}
		} catch {}
	}

	beforeNavigate(({ from, to, willUnload }) => {
		// Flush scroll depth for real page changes only. Same-pathname navigations (shallow routing, back-button drawer) are skipped.
		const isSamePage = from && to && from.url.pathname === to.url.pathname;
		if (!isSamePage) scrollTracker.flush();

		// Reload page if a new version is available
		if (updated.current && !willUnload && to?.url) {
			console.log('Reloading page due to a new version being deployed');
			location.href = to.url.href;
		}

		sendDurationAnalytics(true);
	});

	afterNavigate((navigation) => {
		const route = ClientVelatlasLoggerApi.getRouteMetadata();

		engagementTimer = {
			activeSeconds: 0,
			lastActiveTime: performance.now(),
			isPaused: false,
			uri: route.uri,
			url: route.url
		};

		isPageEntryTracked = false;

		if (navigation.type !== 'enter') {
			hasInteracted = true;
			trackPageEntry();
			return;
		}

		if (hasInteracted) {
			trackPageEntry();
		}
	});

	onMount(() => {
		const botName = getBotName();
		if (botName) {
			analyticsApi.sendAnalytics({
				event: ANALYTICS_EVENT_NAMES.botEntry,
				payload: {
					bot: botName,
					referrer: getCleanReferrer() ?? 'direct'
				}
			});
		}

		// Register interaction listeners IMMEDIATELY on layout mount (captures interactions during top-level await loads)
		const triggerEvents = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll'];

		function handleUserInteraction() {
			triggerEvents.forEach((event) => window.removeEventListener(event, handleUserInteraction));
			markAsHumanInteraction();
		}

		triggerEvents.forEach((event) => window.addEventListener(event, handleUserInteraction, { passive: true }));

		// Monitor browser visibility changes (minimize, tab closure, lock screen)
		const handleVisibilityChange = () => {
			if (document.visibilityState === 'hidden') {
				updateActiveTime();
				engagementTimer.isPaused = true;
				sendDurationAnalytics(true);
			} else {
				engagementTimer.lastActiveTime = performance.now();
				engagementTimer.isPaused = false;
			}
		};

		window.addEventListener('visibilitychange', handleVisibilityChange);
		window.addEventListener('click', handleExternalLinkClick);

		return () => {
			triggerEvents.forEach((event) => window.removeEventListener(event, handleUserInteraction));
			window.removeEventListener('visibilitychange', handleVisibilityChange);
			window.removeEventListener('click', handleExternalLinkClick);
		};
	});

	onDestroy(() => {
		sendDurationAnalytics(true);
	});
</script>

<div
	class="flex min-h-screen flex-col bg-base-100 font-sans text-base-content antialiased selection:bg-primary selection:text-primary-content"
>
	<NavBar {isOverlay} />

	<main {@attach scrollTracker.attach} class="flex w-full grow flex-col">
		{@render children()}
	</main>

	<BackToTop />

	<Footer />
</div>
